import { useState, useEffect, lazy, Suspense } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { PortableText } from '@portabletext/react';
import Container from '../../../components/shared/Container';
import Button from '../../../components/shared/Button';
import SEO from '../../../components/shared/SEO';
import StructuredData from '../../../components/shared/StructuredData';
import { getMenuItemBySlug, getRelatedMenuItems, getLocations } from '../../../lib/sanity';
import { trackOnlineOrderClick, trackReservationClick } from '../../../utils/gtm';
import { generateMenuItemSchema, generateBreadcrumbSchema } from '../../../utils/schema';
import { getSanityImageProps } from '../../../utils/sanityImage';
import { sortLocationsByDefault } from '../../../utils/locationSorting';

// Lazy load the location modal since it contains Mapbox
const LocationSelectionModal = lazy(() => import('../../../components/shared/LocationSelectionModal'));

// Helper function to extract plain text from portable text
const extractPlainText = (portableText) => {
  if (!portableText) return '';
  if (typeof portableText === 'string') return portableText;
  if (Array.isArray(portableText)) {
    return portableText
      .map(block => {
        if (block._type === 'block' && block.children) {
          return block.children.map(child => child.text).join('');
        }
        return '';
      })
      .join(' ');
  }
  return '';
};

export default function MenuItemPage() {
  const { category: categorySlug, item: itemSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuItem, setMenuItem] = useState(null);
  const [relatedItems, setRelatedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for location selection modal
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  // This component is only used for direct/external access (SEO)

  useEffect(() => {
    async function fetchMenuItemData() {
      try {
        setLoading(true);
        const item = await getMenuItemBySlug(itemSlug);
        
        if (!item) {
          setError('Menu item not found');
          return;
        }
        
        // Verify the category slug matches the item's category
        if (item.category && item.category.slug !== categorySlug) {
          // Redirect to the correct URL if the category doesn't match
          navigate(`/menu/${item.category.slug}/${itemSlug}`, { replace: true });
          return;
        }
        
        setMenuItem(item);
        
        // Fetch related items from the same category
        if (item.category && item.category._id) {
          const related = await getRelatedMenuItems(item._id, item.category._id, 2);
          setRelatedItems(related || []);
        }
        
        setError(null);
      } catch (err) {
        console.error(`Error fetching menu item ${itemSlug}:`, err);
        setError('Failed to load menu item. Please try again later.');
      } finally {
        setLoading(false);
      }
    }

    if (itemSlug) {
      fetchMenuItemData();
    }
  }, [itemSlug, categorySlug, navigate]);
  
  // Fetch locations when needed
  useEffect(() => {
    async function fetchLocations() {
      if (locationModalOpen && locations.length === 0) {
        setLoadingLocations(true);
        try {
          const fetchedLocations = await getLocations();
          const sortedData = sortLocationsByDefault(fetchedLocations || []);
          setLocations(sortedData);
        } catch (error) {
          console.error('Error fetching locations:', error);
        } finally {
          setLoadingLocations(false);
        }
      }
    }
    
    fetchLocations();
  }, [locationModalOpen, locations.length]);
  
  // Handle opening the location selection modal
  const handleOpenLocationModal = () => {
    setLocationModalOpen(true);
  };

  if (loading) {
    return (
      <div className="bg-brand-cream min-h-screen">
        <Container>
          <div className="py-32 text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-navy"></div>
            <p className="mt-4 text-brand-navy">Loading menu item...</p>
          </div>
        </Container>
      </div>
    );
  }

  if (error || !menuItem) {
    return (
      <div className="bg-brand-cream min-h-screen">
        <Container>
          <div className="py-32 text-center">
            <h1 className="text-3xl font-heading text-brand-navy mb-4">Menu Item Not Found</h1>
            <p className="text-gray-700 mb-6">{error || "We couldn't find the menu item you're looking for."}</p>
            <Link to="/menu" className="text-brand-navy underline">Return to Menu</Link>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="bg-brand-cream min-h-screen">
      <SEO 
        title={`${menuItem.name} | ${menuItem.category?.name || 'Menu'} | Original Pancake House`}
        description={extractPlainText(menuItem.description) || `Discover our delicious ${menuItem.name} at The Original Pancake House. ${menuItem.category?.name ? `Part of our ${menuItem.category.name} selection.` : ''} Made fresh with quality ingredients.`}
        openGraph={{
          type: 'article',
          image: menuItem.imageUrl || "/oph-social-share2.jpg"
        }}
      />
      
      {/* Structured Data for SEO */}
      <StructuredData data={[
        generateMenuItemSchema(menuItem, categorySlug),
        generateBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Menu', url: '/menu' },
          { name: menuItem.category?.name || 'Menu', url: `/menu/${categorySlug}` },
          { name: menuItem.name, url: `/menu/${categorySlug}/${itemSlug}` }
        ])
      ]} />
      
      {/* Breadcrumb Navigation */}
      <div className="bg-brand-cream pt-32 pb-4">
        <Container>
          <div className="flex flex-wrap items-center text-sm font-medium gap-y-1">
            <Link 
              to="/menu" 
              className="text-brand-navy hover:text-brand-orange transition-colors"
            >
              Menu
            </Link>
            
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mx-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            
            <Link 
              to={`/menu/${categorySlug}`} 
              className="text-brand-navy hover:text-brand-orange transition-colors"
            >
              {menuItem.category && menuItem.category.name ? menuItem.category.name : 'Category'}
            </Link>
            
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mx-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            
            <span className="text-gray-500">
              {menuItem.name}
            </span>
          </div>
        </Container>
      </div>

      {/* Menu Item Detail - Matches drawer layout */}
      <div className="bg-brand-cream py-8">
        <Container>
          {/* Desktop: Side-by-side layout | Mobile: Stacked layout */}
          <div className="flex flex-col lg:flex-row lg:gap-8">
            {/* Image with 1:1 aspect ratio on left */}
            <div className="lg:w-1/2 xl:w-2/5 mb-6 lg:mb-0">
              <div className="relative w-full aspect-square">
                {menuItem.imageUrl ? (
                  <img 
                    {...getSanityImageProps(menuItem.imageUrl, 'menuDetail')}
                    alt={menuItem.name} 
                    className="w-full h-full object-cover object-center rounded-xl shadow-sm"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-100 flex items-center justify-center rounded-xl">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
              </div>
            </div>
            
            {/* Menu Item Content on right - bottom aligned */}
            <div className="lg:w-1/2 xl:w-3/5 lg:flex lg:flex-col lg:justify-end">
              
              <div className="mb-2">
                <h1 className="text-3xl md:text-4xl font-heading text-brand-navy">{menuItem.name}</h1>
                {menuItem.calories && (
                  <div className="text-gray-600 text-sm mt-1">
                    <span>{menuItem.calories} calories</span>
                  </div>
                )}
              </div>
              
              {/* Dietary Tags */}
              {menuItem.dietary && menuItem.dietary.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {menuItem.dietary.map(diet => (
                    <span 
                      key={diet} 
                      className="inline-block bg-white text-brand-navy px-3 py-1 rounded-full text-sm font-medium"
                    >
                      {diet.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                    </span>
                  ))}
                </div>
              )}
              
              {menuItem.plainDescription && (
                <div className="prose prose-lg max-w-none text-gray-700 mb-6">
                  <p>{menuItem.plainDescription}</p>
                </div>
              )}
              
              {/* CTA Buttons - moved below description */}
              <div className="flex flex-wrap gap-3 mb-6">
                <Button 
                  variant="solid" 
                  color="navy"
                  onClick={() => {
                    trackOnlineOrderClick(menuItem?.name || 'Unknown', 'menu_item_page');
                    handleOpenLocationModal();
                  }}
                  icon={
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  }
                >
                  Order Online
                </Button>
                
                <Button 
                  variant="outline" 
                  color="navy"
                  onClick={() => {
                    trackReservationClick(menuItem?.name || 'Unknown', 'menu_item_page');
                    handleOpenLocationModal();
                  }}
                  icon={
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  }
                >
                  Join Waitlist
                </Button>
              </div>
              
            </div>
          </div>
        </Container>
      </div>
      
      {/* Availability Section - Full width below image and content */}
      <div className="bg-gray-50 py-8">
        <Container>
          <h3 className="text-lg font-heading text-brand-navy mb-4">Available at these locations:</h3>
          
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
            {menuItem.locations && menuItem.locations.length > 0 ? (
              menuItem.locations.map(location => (
                <Link 
                  key={location._id}
                  to={location.slug ? `/locations/${location.slug}` : `/locations`}
                  className="flex items-center p-4 bg-white rounded-md hover:shadow-md transition-all"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-brand-navy mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="text-sm font-medium text-brand-navy truncate">{location.name}</span>
                </Link>
              ))
            ) : (
              <div className="col-span-2 lg:col-span-3 p-4 bg-white rounded-md text-sm text-gray-600">
                Available at all locations
              </div>
            )}
          </div>
        </Container>
      </div>

      {/* Related Items - Matches drawer style */}
      {relatedItems && relatedItems.length > 0 && (
        <div className="bg-brand-navy py-8">
          <Container>
            <h2 className="text-xl font-heading text-white mb-4">You May Also Like</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {relatedItems.map((item) => (
                <Link 
                  key={item._id}
                  to={`/menu/${categorySlug}/${item.slug}`}
                  className="bg-white rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                >
                  <div className="h-32 overflow-hidden">
                    {item.imageUrl ? (
                      <img 
                        src={item.imageUrl} 
                        alt={item.name} 
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-brand-navy">{item.name}</h3>
                    {item.plainDescription && (
                      <p className="mt-1 text-sm text-gray-600 line-clamp-2">{item.plainDescription}</p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </Container>
        </div>
      )}
      
      {/* Location Selection Modal */}
      <Suspense fallback={null}>
        <LocationSelectionModal
          isOpen={locationModalOpen}
          onClose={() => setLocationModalOpen(false)}
          locations={locations}
        />
      </Suspense>
    </div>
  );
}
